import { QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { Column, ColumnConfig } from '@ant-design/charts';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  ChannelTag,
  GET_BUSINESS_AREA_TAGS,
  GET_CHANNEL_TAGS,
  GET_IDEA_COUNTS_BY_STAGES,
  GET_TOPICS,
  IdeaCountByStage,
  PocBusinessArea,
  Topic,
} from '@frontend/data-access';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { RichEditorSerialized } from '@frontend/shared/ui';
import { AutoCompleteProps, Card, Select, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

enum IdeaCountType {
  BusinessArea = 'BusinessArea',
  Topics = 'Topics',
  ChannelTagIds = 'ChannelTagIds',
}

export interface IdeaCountsByStagesProps {
  channelId?: string;
  className?: string;
  challengeId?: string;
  innovationSpaceId?: string;
}

export function IdeaCountsByStages({
  channelId,
  className,
  challengeId,
  innovationSpaceId,
}: IdeaCountsByStagesProps) {
  const { t } = useTranslation('common');
  const items = [
    {
      label: t('business_area'),
      value: IdeaCountType.BusinessArea,
    },
    {
      label: t('topics'),
      value: IdeaCountType.Topics,
    },
    {
      label: t('channel_tag'),
      value: IdeaCountType.ChannelTagIds,
    },
  ];

  const [getCountByStage, { data, loading }] = useLazyQuery<{
    channelIdeaCountByStage: IdeaCountByStage[];
  }>(GET_IDEA_COUNTS_BY_STAGES);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [options, setOptions] = useState<AutoCompleteProps['options']>([]);
  const [selectedType, setSelectedType] = useState<IdeaCountType>();

  const { data: businessArea } = useQuery<{
    pocBusinessAreaTags: PocBusinessArea[];
  }>(GET_BUSINESS_AREA_TAGS);

  const { data: dataTopics } = useQuery<{
    topics: Topic[];
  }>(GET_TOPICS);

  const { data: channelTags } = useQuery<{ channelTags: ChannelTag[] }>(
    GET_CHANNEL_TAGS
  );

  const searchApi = useCallback(
    (query: string) => {
      switch (selectedType) {
        case IdeaCountType.BusinessArea:
          setOptions(
            businessArea?.pocBusinessAreaTags.map((businessArea) => ({
              value: businessArea.name,
              label: businessArea.name,
            })) || []
          );
          break;
        case IdeaCountType.Topics:
          setOptions(
            dataTopics?.topics.map((topic) => ({
              value: topic.id,
              label: topic.name,
            })) || []
          );
          break;
        case IdeaCountType.ChannelTagIds:
          setOptions(
            channelTags?.channelTags.map((tag) => ({
              value: tag.id,
              label: tag.name,
            })) || []
          );
          break;
        default:
          break;
      }
    },
    [
      selectedType,
      businessArea?.pocBusinessAreaTags,
      dataTopics?.topics,
      channelTags?.channelTags,
    ]
  );

  useEffect(() => {
    if (!channelId) return;
    const input: Record<string, string[] | string> = {};

    switch (selectedType) {
      case IdeaCountType.BusinessArea:
        if (selectedItems?.length > 0) {
          input['businessArea'] = selectedItems;
        }
        break;
      case IdeaCountType.Topics:
        if (selectedItems?.length > 0) {
          input['topicIds'] = selectedItems;
        }
        break;
      case IdeaCountType.ChannelTagIds:
        if (selectedItems?.length > 0) {
          input['channelTagIds'] = selectedItems;
        }
        break;
      default:
        break;
    }
    if (challengeId !== undefined) {
      input['challengeIds'] = [challengeId];
    } else {
      delete input['challengeIds'];
    }
    getCountByStage({
      variables: {
        input: {
          ...input,
          channelId,
          spaceId: innovationSpaceId,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, channelId, challengeId, innovationSpaceId]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleChange = (values: string[]) => {
    setSelectedItems(values);
  };

  const handleChangeType = (value: IdeaCountType) => {
    setSearchTerm('');
    setSelectedItems([]);
    setSelectedType(value);
  };

  const chartData = useMemo(() => {
    const realData = data?.channelIdeaCountByStage || [];
    if (realData.length > 0) {
      return realData;
    }
    // Return placeholder data with 0 count when no data is available
    return [{ phase: t('no_data'), items: 0 }];
  }, [data, t]);

  const config: ColumnConfig = {
    data: chartData,
    loading: loading,
    xField: 'phase',
    yField: 'items',
    legend: false,
    color: '#529FF0',
    paddingTop: 20,
    label: {
      text: 'items',
      position: 'top',
      textAlign: 'center',
      dy: -15,
      style: {
        fontFamily: 'Lufga',
      },
    },
    // Ensure y-axis starts from 0 and includes at least value 1
    yAxis: {
      min: 0,
      max: chartData.some((item) => item.items > 0) ? undefined : 1,
    },
  };

  useEffect(() => {
    setSelectedItems([]);
  }, [channelId]);

  useEffect(() => {
    searchApi(searchTerm);
    // if (!searchTerm) return;

    // const delayDebounceFn = setTimeout(() => {
    //   setIsLoading(true);
    //   searchApi(searchTerm);
    // }, WAIT_INTERVAL);

    // return () => clearTimeout(delayDebounceFn);
  }, [searchApi, searchTerm, selectedType]);

  useEffect(() => {
    handleChangeType(IdeaCountType.BusinessArea);
  }, []);

  return (
    <div className={className}>
      <Card style={{ borderRadius: 20 }}>
        <div className="space-y-4">
          <div>
            <div className="text-sm text-gray-900 font-medium">
              {t('idea_counts_by_stages')}
            </div>
            <div className="mt-6 mb-2 flex items-center gap-2">
              <label className="font-normal">
                {t('search_to_select_filter')}
              </label>
              <Tooltip
                title={() => (
                  <RichEditorSerialized value={t('search_tooltip')} />
                )}
              >
                <QuestionCircleOutlined className="cursor-pointer" />
              </Tooltip>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select
                showSearch
                onChange={handleChange}
                onSearch={handleSearch}
                options={options}
                value={selectedItems}
                suffixIcon={<SearchOutlined />}
                maxTagCount="responsive"
                placeholder={t('search_by_name')}
                allowClear
                mode={
                  selectedType === IdeaCountType.BusinessArea
                    ? undefined
                    : 'multiple'
                }
                className="flex-1 !w-full"
              />
              <Select
                defaultValue={IdeaCountType.BusinessArea}
                className="!w-[200px]"
                onChange={handleChangeType}
                options={items}
              />
            </div>
          </div>
          <Column {...config} />
        </div>
      </Card>
    </div>
  );
}

export default IdeaCountsByStages;
